import { useEffect } from 'react'
import './DocumentPage.css'

const Advanced = () => {
  useEffect(() => {
    const headings = document.querySelectorAll('.doc-content h2, .doc-content h3')
    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = heading.textContent.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
      }
    })
  }, [])

  return (
    <div className="doc-page">
      <div className="doc-content">
        <h1>Advanced Documentation</h1>
        <p className="lead">
          Deep dive into advanced features, API reference, and best practices for power users.
        </p>

        <h2>Advanced Topics</h2>
        <p>
          This section covers advanced usage of the Variable Modding System including custom
          data structures, performance optimization, and integration with other systems.
        </p>

        <h2>Custom Data Structures</h2>
        <h3>Creating Custom Struct Types</h3>
        <p>
          Define your own data structures for complex variable types:
        </p>
        <pre><code>{`USTRUCT(BlueprintType)
struct FCustomVariableData
{
    GENERATED_BODY()

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    FString Name;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    float Value;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    bool bIsActive;
};`}</code></pre>

        <h3>Nested Data Tables</h3>
        <p>
          Link multiple data tables together for complex data relationships.
        </p>

        <h2>Performance Optimization</h2>
        <h3>Caching Strategies</h3>
        <p>Best practices for caching frequently accessed variables:</p>
        <ul>
          <li>Cache references to data tables at startup</li>
          <li>Use async loading for large datasets</li>
          <li>Implement pooling for variable objects</li>
        </ul>

        <h3>Memory Management</h3>
        <p>Optimize memory usage:</p>
        <ul>
          <li>Unload unused data tables</li>
          <li>Use soft references for large assets</li>
          <li>Implement lazy loading where appropriate</li>
        </ul>

        <h2>Blueprint Integration</h2>
        <h3>Accessing Variables in Blueprints</h3>
        <p>Use Blueprint nodes to read and write variables efficiently.</p>

        <h3>Creating Custom Blueprint Functions</h3>
        <p>Extend functionality with custom Blueprint-callable functions.</p>

        <h2>C++ API Reference</h2>
        <h3>Core Classes</h3>
        <ul>
          <li><code>UVariableModdingSubsystem</code> - Main subsystem interface</li>
          <li><code>UVariableDataTable</code> - Data table wrapper</li>
          <li><code>FVariableHandle</code> - Variable reference handle</li>
        </ul>

        <h3>Common Functions</h3>
        <pre><code>{`// Get variable value
GetVariableValue(FName VariableName);

// Set variable value
SetVariableValue(FName VariableName, FVariant Value);

// Load data table
LoadDataTable(FSoftObjectPath TablePath);`}</code></pre>

        <h2>Debugging and Profiling</h2>
        <h3>Debug Commands</h3>
        <p>Useful console commands:</p>
        <ul>
          <li><code>VariableModding.DumpAll</code> - List all variables</li>
          <li><code>VariableModding.ShowStats</code> - Display statistics</li>
          <li><code>VariableModding.ValidateData</code> - Check data integrity</li>
        </ul>

        <h3>Profiling</h3>
        <p>Use Unreal's profiling tools to monitor variable system performance.</p>

        <h2>Advanced Workflows</h2>
        <h3>Version Control Integration</h3>
        <p>Best practices for managing variable data in version control systems.</p>

        <h3>Automated Testing</h3>
        <p>Set up automated tests for your variable configurations.</p>

        <h3>Continuous Integration</h3>
        <p>Integrate variable validation into your CI/CD pipeline.</p>

        <h2>Security Considerations</h2>
        <ul>
          <li>Validate all imported data</li>
          <li>Protect sensitive variables from modification</li>
          <li>Implement access control for administrative variables</li>
          <li>Encrypt sensitive data in shipped builds</li>
        </ul>
      </div>
    </div>
  )
}

export default Advanced
